import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: (failureCount, error) => {
        if (error && typeof error === 'object') {
          const status =
            ('response' in error && typeof error.response === 'object' && error.response && 'status' in error.response
              ? (error.response as { status: number }).status
              : undefined)
            ?? (error as { status?: number }).status;

          // Do not retry auth/permission failures or rate-limit responses.
          // The apiClient interceptor already handles 401 refresh and a single
          // 429 retry; letting React Query retry those would cause loops or
          // additional hammering.
          if (status === 401 || status === 403 || status === 404 || status === 429) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

export const queryKeys = {
  assets: {
    all: ['assets'] as const,
    lists: () => [...queryKeys.assets.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.assets.lists(), filters] as const,
    details: () => [...queryKeys.assets.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.assets.details(), id] as const,
    summary: () => [...queryKeys.assets.all, 'summary'] as const,
    map: (filters: Record<string, unknown>) => [...queryKeys.assets.all, 'map', filters] as const,
    history: (id: number) => [...queryKeys.assets.detail(id), 'history'] as const,
    problemReports: (id: number) =>
      [...queryKeys.assets.detail(id), 'problem-reports'] as const,
    inspections: (id: number) =>
      [...queryKeys.assets.detail(id), 'inspections'] as const,
    inspectionOverrun: (locationId: number, windowDays = 0) =>
      [...queryKeys.assets.all, 'inspection-overrun', locationId, windowDays] as const,
  },
  inspections: {
    all: ['inspections'] as const,
    lists: () => [...queryKeys.inspections.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.inspections.lists(), filters] as const,
    details: () => [...queryKeys.inspections.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.inspections.details(), id] as const,
    summary: () => [...queryKeys.inspections.all, 'summary'] as const,
  },
  deployments: {
    all: ['deployments'] as const,
    lists: () => [...queryKeys.deployments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.deployments.lists(), filters] as const,
    details: () => [...queryKeys.deployments.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.deployments.details(), id] as const,
  },
  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.locations.lists(), filters] as const,
    details: () => [...queryKeys.locations.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.locations.details(), id] as const,
  },
  ropeCuts: {
    all: ['rope-cuts'] as const,
    lists: () => [...queryKeys.ropeCuts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.ropeCuts.lists(), filters] as const,
    details: () => [...queryKeys.ropeCuts.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.ropeCuts.details(), id] as const,
    summary: () => [...queryKeys.ropeCuts.all, 'summary'] as const,
  },
  maintenance: {
    all: ['maintenance'] as const,
    lists: () => [...queryKeys.maintenance.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.maintenance.lists(), filters] as const,
    details: () => [...queryKeys.maintenance.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.maintenance.details(), id] as const,
  },
  certifications: {
    all: ['certifications'] as const,
    lists: () => [...queryKeys.certifications.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.certifications.lists(), filters] as const,
    details: () => [...queryKeys.certifications.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.certifications.details(), id] as const,
  },
  reservations: {
    all: ['reservations'] as const,
    lists: () => [...queryKeys.reservations.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.reservations.lists(), filters] as const,
    details: () => [...queryKeys.reservations.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.reservations.details(), id] as const,
  },
  templates: {
    all: ['templates'] as const,
    lists: () => [...queryKeys.templates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.templates.lists(), filters] as const,
    details: () => [...queryKeys.templates.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.templates.details(), id] as const,
  },
  work: {
    all: ['work'] as const,
    taskLists: () => [...queryKeys.work.all, 'tasks'] as const,
    taskList: (filters: Record<string, unknown>) =>
      [...queryKeys.work.taskLists(), filters] as const,
    taskDetail: (id: number | 'new') => [...queryKeys.work.all, 'task', id] as const,
    taskSteps: (taskId: number | 'new') =>
      [...queryKeys.work.all, 'task', taskId, 'steps'] as const,
    templateLists: () => [...queryKeys.work.all, 'templates'] as const,
    templateList: (filters: Record<string, unknown>) =>
      [...queryKeys.work.templateLists(), filters] as const,
    templateDetail: (id: number | 'new') =>
      [...queryKeys.work.all, 'template', id] as const,
    history: (filters: Record<string, unknown>) =>
      [...queryKeys.work.all, 'history', filters] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.users.details(), id] as const,
    profile: () => [...queryKeys.users.all, 'profile'] as const,
  },
  notificationPreferences: {
    all: ['notification-preferences'] as const,
    list: () => [...queryKeys.notificationPreferences.all, 'list'] as const,
  },
  invitations: {
    all: ['invitations'] as const,
    lists: () => [...queryKeys.invitations.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.invitations.lists(), filters] as const,
    details: () => [...queryKeys.invitations.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.invitations.details(), id] as const,
  },
  auditLogs: {
    all: ['audit-logs'] as const,
    lists: () => [...queryKeys.auditLogs.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.auditLogs.lists(), filters] as const,
  },
  settings: {
    all: ['settings'] as const,
    organization: () => [...queryKeys.settings.all, 'organization'] as const,
    modules: () => [...queryKeys.settings.all, 'modules'] as const,
    industry: () => [...queryKeys.settings.all, 'industry'] as const,
  },
  branding: {
    all: ['branding'] as const,
    current: () => [...queryKeys.branding.all, 'current'] as const,
  },
  assetTemplates: {
    all: ['asset-templates'] as const,
    lists: () => [...queryKeys.assetTemplates.all, 'list'] as const,
    list: () => [...queryKeys.assetTemplates.lists()] as const,
    details: () => [...queryKeys.assetTemplates.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.assetTemplates.details(), id] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    summary: () => [...queryKeys.dashboard.all, 'summary'] as const,
    platform: () => [...queryKeys.dashboard.all, 'platform'] as const,
  },
  compliance: {
    all: ['compliance'] as const,
    data: () => [...queryKeys.compliance.all, 'data'] as const,
    quarterlyStats: () => [...queryKeys.compliance.all, 'quarterly-stats'] as const,
    quarterlyStatsList: (filters: Record<string, unknown>) =>
      [...queryKeys.compliance.quarterlyStats(), 'list', filters] as const,
    quarterlyStatsDetail: (id: number) =>
      [...queryKeys.compliance.quarterlyStats(), 'detail', id] as const,
    safetyKpis: () => [...queryKeys.compliance.all, 'safety-kpis'] as const,
    safetyKpiTrends: (filters: Record<string, unknown>) =>
      [...queryKeys.compliance.safetyKpis(), 'trends', filters] as const,
    dashboard: () => [...queryKeys.compliance.all, 'dashboard'] as const,
    summary: () => [...queryKeys.compliance.all, 'summary'] as const,
    urgentActions: () => [...queryKeys.compliance.all, 'urgent-actions'] as const,
    upcomingDeadlines: () => [...queryKeys.compliance.all, 'upcoming-deadlines'] as const,
    sections: () => [...queryKeys.compliance.all, 'sections'] as const,
    alerts: () => [...queryKeys.compliance.all, 'alerts'] as const,
    alertsList: (filters: Record<string, unknown>) =>
      [...queryKeys.compliance.alerts(), 'list', filters] as const,
    pendingProposal: () => [...queryKeys.compliance.all, 'pending-proposal'] as const,
    acceptAllUndoBatches: () => [...queryKeys.compliance.all, 'accept-all-undo-batches'] as const,
    proposedEvidence: () => [...queryKeys.compliance.all, 'proposed-evidence'] as const,
  },
  complianceIncidents: {
    all: ['compliance-incidents'] as const,
    lists: () => [...queryKeys.complianceIncidents.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.complianceIncidents.lists(), filters] as const,
    details: () => [...queryKeys.complianceIncidents.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.complianceIncidents.details(), id] as const,
  },
  auditPrep: {
    all: ['audit-prep'] as const,
    lists: () => [...queryKeys.auditPrep.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.auditPrep.lists(), filters] as const,
    details: () => [...queryKeys.auditPrep.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.auditPrep.details(), id] as const,
    fm333Data: (id: number) => [...queryKeys.auditPrep.all, 'fm333-data', id] as const,
  },
  correctiveActions: {
    all: ['corrective-actions'] as const,
    lists: () => [...queryKeys.correctiveActions.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.correctiveActions.lists(), filters] as const,
    details: () => [...queryKeys.correctiveActions.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.correctiveActions.details(), id] as const,
    overdue: () => [...queryKeys.correctiveActions.all, 'overdue'] as const,
    assignedToMe: () => [...queryKeys.correctiveActions.all, 'assigned-to-me'] as const,
  },
  managementReviews: {
    all: ['management-reviews'] as const,
    lists: () => [...queryKeys.managementReviews.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.managementReviews.lists(), filters] as const,
    details: () => [...queryKeys.managementReviews.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.managementReviews.details(), id] as const,
    agendaSuggestions: () => [...queryKeys.managementReviews.all, 'agenda-suggestions'] as const,
  },
  safetyCommunications: {
    all: ['safety-communications'] as const,
    lists: () => [...queryKeys.safetyCommunications.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.safetyCommunications.lists(), filters] as const,
    details: () => [...queryKeys.safetyCommunications.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.safetyCommunications.details(), id] as const,
    ackStatus: (id: number) =>
      [...queryKeys.safetyCommunications.detail(id), 'ack-status'] as const,
  },
  consultantOverview: {
    all: ['consultant-overview'] as const,
    data: () => [...queryKeys.consultantOverview.all, 'data'] as const,
  },
  clauseStatus: {
    all: ['clause-status'] as const,
    lists: () => [...queryKeys.clauseStatus.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.clauseStatus.lists(), filters] as const,
    details: () => [...queryKeys.clauseStatus.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.clauseStatus.details(), id] as const,
    evidence: (id: number) => [...queryKeys.clauseStatus.detail(id), 'evidence'] as const,
    auditHistory: (clauseRef: string) => [...queryKeys.clauseStatus.all, 'audit-history', clauseRef] as const,
    actions: (id: number) => [...queryKeys.clauseStatus.detail(id), 'actions'] as const,
  },
  complianceProfile: {
    all: ['compliance-profile'] as const,
    data: () => [...queryKeys.complianceProfile.all, 'data'] as const,
  },
  nominatedPersonnel: {
    all: ['nominated-personnel'] as const,
    lists: () => [...queryKeys.nominatedPersonnel.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.nominatedPersonnel.lists(), filters] as const,
    details: () => [...queryKeys.nominatedPersonnel.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.nominatedPersonnel.details(), id] as const,
  },
  insurancePolicies: {
    all: ['insurance-policies'] as const,
    lists: () => [...queryKeys.insurancePolicies.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.insurancePolicies.lists(), filters] as const,
    details: () => [...queryKeys.insurancePolicies.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.insurancePolicies.details(), id] as const,
  },
  legalEntity: {
    all: ['legal-entity'] as const,
    lists: () => [...queryKeys.legalEntity.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.legalEntity.lists(), filters] as const,
    details: () => [...queryKeys.legalEntity.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.legalEntity.details(), id] as const,
  },
  controlledDocuments: {
    all: ['controlled-documents'] as const,
    lists: () => [...queryKeys.controlledDocuments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.controlledDocuments.lists(), filters] as const,
    details: () => [...queryKeys.controlledDocuments.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.controlledDocuments.details(), id] as const,
    ownerOptions: () => [...queryKeys.controlledDocuments.all, 'owner-options'] as const,
    acknowledgments: (id: number) =>
      [...queryKeys.controlledDocuments.detail(id), 'acknowledgments'] as const,
  },
  search: {
    all: ['search'] as const,
    results: (query: string) => [...queryKeys.search.all, query] as const,
    global: (query: string) => [...queryKeys.search.all, 'global', query] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
  },
  crossModule: {
    all: ['cross-module'] as const,
    projectSafety: (id: number) => [...queryKeys.crossModule.all, 'project', id, 'safety'] as const,
    projectGear: (id: number) => [...queryKeys.crossModule.all, 'project', id, 'gear'] as const,
    projectCrew: (id: number) => [...queryKeys.crossModule.all, 'project', id, 'crew'] as const,
    techProjects: (id: number) => [...queryKeys.crossModule.all, 'tech', id, 'projects'] as const,
    techSafety: (id: number) => [...queryKeys.crossModule.all, 'tech', id, 'safety'] as const,
  },
  reports: {
    all: ['reports'] as const,
    utilization: (filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, 'utilization', filters] as const,
    inspectionCompliance: (filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, 'inspection-compliance', filters] as const,
    ropeCutTrends: (filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, 'rope-cut-trends', filters] as const,
    gearForecast: (filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, 'gear-forecast', filters] as const,
  },
  kits: {
    all: ['kits'] as const,
    lists: () => [...queryKeys.kits.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.kits.lists(), filters] as const,
    details: () => [...queryKeys.kits.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.kits.details(), id] as const,
  },
  gearModels: {
    all: ['gear-models'] as const,
    lists: () => [...queryKeys.gearModels.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.gearModels.lists(), filters] as const,
    details: () => [...queryKeys.gearModels.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.gearModels.details(), id] as const,
  },
  gearRates: {
    all: ['gear-rates'] as const,
    lists: () => [...queryKeys.gearRates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.gearRates.lists(), filters] as const,
  },
  manufacturers: {
    all: ['manufacturers'] as const,
    lists: () => [...queryKeys.manufacturers.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.manufacturers.lists(), filters] as const,
    details: () => [...queryKeys.manufacturers.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.manufacturers.details(), id] as const,
  },
  jsas: {
    all: ['jsas'] as const,
    lists: () => [...queryKeys.jsas.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.jsas.lists(), filters] as const,
    details: () => [...queryKeys.jsas.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.jsas.details(), id] as const,
  },
  jsaArchive: {
    all: ['jsa-archive'] as const,
    lists: () => [...queryKeys.jsaArchive.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.jsaArchive.lists(), filters] as const,
  },
  legacyJsas: {
    all: ['legacy-jsas'] as const,
    lists: () => [...queryKeys.legacyJsas.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.legacyJsas.lists(), filters] as const,
    details: () => [...queryKeys.legacyJsas.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.legacyJsas.details(), id] as const,
  },
  safeWorkPlans: {
    all: ['safe-work-plans'] as const,
    lists: () => [...queryKeys.safeWorkPlans.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.safeWorkPlans.lists(), filters] as const,
    details: () => [...queryKeys.safeWorkPlans.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.safeWorkPlans.details(), id] as const,
  },
  rescuePlans: {
    all: ['rescue-plans'] as const,
    lists: () => [...queryKeys.rescuePlans.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.rescuePlans.lists(), filters] as const,
    details: () => [...queryKeys.rescuePlans.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.rescuePlans.details(), id] as const,
  },
  toolboxTalks: {
    all: ['toolbox-talks'] as const,
    lists: () => [...queryKeys.toolboxTalks.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.toolboxTalks.lists(), filters] as const,
    details: () => [...queryKeys.toolboxTalks.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.toolboxTalks.details(), id] as const,
  },
  toolboxTalkTemplates: {
    all: ['toolbox-talk-templates'] as const,
    lists: () => [...queryKeys.toolboxTalkTemplates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.toolboxTalkTemplates.lists(), filters] as const,
    details: () => [...queryKeys.toolboxTalkTemplates.all, 'detail'] as const,
    detail: (id: number) =>
      [...queryKeys.toolboxTalkTemplates.details(), id] as const,
  },
  toolboxAnalytics: {
    all: ['toolbox-analytics'] as const,
    data: (filters: Record<string, unknown>) =>
      [...queryKeys.toolboxAnalytics.all, 'data', filters] as const,
  },
  nearMisses: {
    all: ['near-misses'] as const,
    lists: () => [...queryKeys.nearMisses.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.nearMisses.lists(), filters] as const,
    details: () => [...queryKeys.nearMisses.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.nearMisses.details(), id] as const,
  },
  safetyTemplates: {
    all: ['safety-templates'] as const,
    lists: () => [...queryKeys.safetyTemplates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.safetyTemplates.lists(), filters] as const,
    details: () => [...queryKeys.safetyTemplates.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.safetyTemplates.details(), id] as const,
  },
  techProfiles: {
    all: ['tech-profiles'] as const,
    lists: () => [...queryKeys.techProfiles.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.techProfiles.lists(), filters] as const,
    details: () => [...queryKeys.techProfiles.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.techProfiles.details(), id] as const,
  },
  techCertifications: {
    all: ['tech-certifications'] as const,
    lists: () => [...queryKeys.techCertifications.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.techCertifications.lists(), filters] as const,
    details: () => [...queryKeys.techCertifications.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.techCertifications.details(), id] as const,
  },
  availability: {
    all: ['availability'] as const,
    lists: () => [...queryKeys.availability.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.availability.lists(), filters] as const,
  },
  crewAssignments: {
    all: ['crew-assignments'] as const,
    lists: () => [...queryKeys.crewAssignments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.crewAssignments.lists(), filters] as const,
    details: () => [...queryKeys.crewAssignments.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.crewAssignments.details(), id] as const,
  },
  travelItineraries: {
    all: ['travel-itineraries'] as const,
    lists: () => [...queryKeys.travelItineraries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.travelItineraries.lists(), filters] as const,
    details: () => [...queryKeys.travelItineraries.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.travelItineraries.details(), id] as const,
    my: () => [...queryKeys.travelItineraries.all, 'my'] as const,
  },
  travelRecords: {
    all: ['travel-records'] as const,
    lists: () => [...queryKeys.travelRecords.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.travelRecords.lists(), filters] as const,
    details: () => [...queryKeys.travelRecords.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.travelRecords.details(), id] as const,
  },
  crewLists: {
    all: ['crew-lists'] as const,
    lists: () => [...queryKeys.crewLists.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.crewLists.lists(), filters] as const,
    details: () => [...queryKeys.crewLists.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.crewLists.details(), id] as const,
  },
  crewTemplates: {
    all: ['crew-templates'] as const,
    lists: () => [...queryKeys.crewTemplates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.crewTemplates.lists(), filters] as const,
    details: () => [...queryKeys.crewTemplates.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.crewTemplates.details(), id] as const,
  },
  crewFinders: {
    all: ['crew-finders'] as const,
    lists: () => [...queryKeys.crewFinders.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.crewFinders.lists(), filters] as const,
  },
  departments: {
    all: ['departments'] as const,
    lists: () => [...queryKeys.departments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.departments.lists(), filters] as const,
    details: () => [...queryKeys.departments.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.departments.details(), id] as const,
    rollup: (filters: Record<string, unknown>) =>
      [...queryKeys.departments.all, 'rollup', filters] as const,
  },
  projectClients: {
    all: ['project-clients'] as const,
    lists: () => [...queryKeys.projectClients.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projectClients.lists(), filters] as const,
    details: () => [...queryKeys.projectClients.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.projectClients.details(), id] as const,
  },
  clientContacts: {
    all: ['client-contacts'] as const,
    lists: () => [...queryKeys.clientContacts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.clientContacts.lists(), filters] as const,
  },
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projects.lists(), filters] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.projects.details(), id] as const,
    summary: (id: number) => [...queryKeys.projects.detail(id), 'summary'] as const,
    activity: (id: number, page: number) =>
      [...queryKeys.projects.detail(id), 'activity', page] as const,
    costCodes: (id: number) =>
      [...queryKeys.projects.detail(id), 'cost-codes'] as const,
  },
  projectPhases: {
    all: ['project-phases'] as const,
    lists: () => [...queryKeys.projectPhases.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projectPhases.lists(), filters] as const,
  },
  projectTasks: {
    all: ['project-tasks'] as const,
    lists: () => [...queryKeys.projectTasks.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projectTasks.lists(), filters] as const,
    details: () => [...queryKeys.projectTasks.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.projectTasks.details(), id] as const,
  },
  projectAssignments: {
    all: ['project-assignments'] as const,
    lists: () => [...queryKeys.projectAssignments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projectAssignments.lists(), filters] as const,
  },
  projectNotes: {
    all: ['project-notes'] as const,
    lists: () => [...queryKeys.projectNotes.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projectNotes.lists(), filters] as const,
  },
  projectDocuments: {
    all: ['project-documents'] as const,
    lists: () => [...queryKeys.projectDocuments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projectDocuments.lists(), filters] as const,
  },
  bids: {
    all: ['bids'] as const,
    lists: () => [...queryKeys.bids.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.bids.lists(), filters] as const,
    details: () => [...queryKeys.bids.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.bids.details(), id] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    lists: () => [...queryKeys.invoices.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.invoices.lists(), filters] as const,
    details: () => [...queryKeys.invoices.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.invoices.details(), id] as const,
  },
  invoicePayments: {
    all: ['invoice-payments'] as const,
    lists: () => [...queryKeys.invoicePayments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.invoicePayments.lists(), filters] as const,
  },
  timeEntries: {
    all: ['time-entries'] as const,
    lists: () => [...queryKeys.timeEntries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.timeEntries.lists(), filters] as const,
    details: () => [...queryKeys.timeEntries.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.timeEntries.details(), id] as const,
    summary: (filters: Record<string, unknown>) =>
      [...queryKeys.timeEntries.all, 'summary', filters] as const,
  },
  portalAccess: {
    all: ['portal-access'] as const,
    lists: () => [...queryKeys.portalAccess.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.portalAccess.lists(), filters] as const,
    details: () => [...queryKeys.portalAccess.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.portalAccess.details(), id] as const,
  },
  recurringInvoiceConfigs: {
    all: ['recurring-invoice-configs'] as const,
    lists: () => [...queryKeys.recurringInvoiceConfigs.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.recurringInvoiceConfigs.lists(), filters] as const,
    details: () => [...queryKeys.recurringInvoiceConfigs.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.recurringInvoiceConfigs.details(), id] as const,
  },
  changeOrders: {
    all: ['change-orders'] as const,
    lists: () => [...queryKeys.changeOrders.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.changeOrders.lists(), filters] as const,
    details: () => [...queryKeys.changeOrders.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.changeOrders.details(), id] as const,
  },
  changeOrderLineItems: {
    all: ['change-order-line-items'] as const,
    lists: () => [...queryKeys.changeOrderLineItems.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.changeOrderLineItems.lists(), filters] as const,
  },
  portal: {
    all: ['portal'] as const,
    validate: (token: string) => [...queryKeys.portal.all, token, 'validate'] as const,
    projects: (token: string) => [...queryKeys.portal.all, token, 'projects'] as const,
    bids: (token: string) => [...queryKeys.portal.all, token, 'bids'] as const,
    bid: (token: string, id: number) => [...queryKeys.portal.all, token, 'bid', id] as const,
    invoices: (token: string) => [...queryKeys.portal.all, token, 'invoices'] as const,
    changeOrders: (token: string) => [...queryKeys.portal.all, token, 'change-orders'] as const,
    activity: (token: string, projectId: number, page: number) =>
      [...queryKeys.portal.all, token, 'activity', projectId, page] as const,
    safety: (token: string, projectId: number) => [...queryKeys.portal.all, token, 'safety', projectId] as const,
  },
  expenses: {
    all: ['expenses'] as const,
    lists: () => [...queryKeys.expenses.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.expenses.lists(), filters] as const,
    details: () => [...queryKeys.expenses.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.expenses.details(), id] as const,
    myExpenses: (filters: Record<string, unknown>) =>
      [...queryKeys.expenses.all, 'my-expenses', filters] as const,
  },
  expenseCategories: {
    all: ['expense-categories'] as const,
    lists: () => [...queryKeys.expenseCategories.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.expenseCategories.lists(), filters] as const,
  },
  mileage: {
    all: ['mileage'] as const,
    lists: () => [...queryKeys.mileage.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.mileage.lists(), filters] as const,
    details: () => [...queryKeys.mileage.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.mileage.details(), id] as const,
  },
  perDiem: {
    all: ['per-diem'] as const,
    lists: () => [...queryKeys.perDiem.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.perDiem.lists(), filters] as const,
    details: () => [...queryKeys.perDiem.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.perDiem.details(), id] as const,
  },
  payrollSettings: {
    all: ['payroll-settings'] as const,
    detail: () => [...queryKeys.payrollSettings.all, 'detail'] as const,
  },
  timesheets: {
    all: ['timesheets'] as const,
    lists: () => [...queryKeys.timesheets.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.timesheets.lists(), filters] as const,
    details: () => [...queryKeys.timesheets.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.timesheets.details(), id] as const,
    myTimesheets: (filters: Record<string, unknown>) =>
      [...queryKeys.timesheets.all, 'my', filters] as const,
  },
  jobCost: {
    all: ['job-cost'] as const,
    lists: () => [...queryKeys.jobCost.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.jobCost.lists(), filters] as const,
    details: () => [...queryKeys.jobCost.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.jobCost.details(), id] as const,
  },
  payPeriods: {
    all: ['pay-periods'] as const,
    lists: () => [...queryKeys.payPeriods.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.payPeriods.lists(), filters] as const,
    details: () => [...queryKeys.payPeriods.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.payPeriods.details(), id] as const,
  },
  payrollRuns: {
    all: ['payroll-runs'] as const,
    lists: () => [...queryKeys.payrollRuns.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.payrollRuns.lists(), filters] as const,
    details: () => [...queryKeys.payrollRuns.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.payrollRuns.details(), id] as const,
  },
  techPayRates: {
    all: ['tech-pay-rates'] as const,
    lists: () => [...queryKeys.techPayRates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.techPayRates.lists(), filters] as const,
    details: () => [...queryKeys.techPayRates.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.techPayRates.details(), id] as const,
  },
  accountingExports: {
    all: ['accounting-exports'] as const,
    lists: () => [...queryKeys.accountingExports.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.accountingExports.lists(), filters] as const,
  },
  costCodes: {
    all: ['cost-codes'] as const,
    lists: () => [...queryKeys.costCodes.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.costCodes.lists(), filters] as const,
    details: () => [...queryKeys.costCodes.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.costCodes.details(), id] as const,
  },
  incidents: {
    all: ['incidents'] as const,
    lists: () => [...queryKeys.incidents.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.incidents.lists(), filters] as const,
    details: () => [...queryKeys.incidents.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.incidents.details(), id] as const,
  },
  rescueDrills: {
    all: ['rescue-drills'] as const,
    lists: () => [...queryKeys.rescueDrills.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.rescueDrills.lists(), filters] as const,
    details: () => [...queryKeys.rescueDrills.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.rescueDrills.details(), id] as const,
  },
  safetyDiagrams: {
    all: ['safety-diagrams'] as const,
    lists: () => [...queryKeys.safetyDiagrams.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.safetyDiagrams.lists(), filters] as const,
  },
  safetyMetrics: {
    all: ['safety-metrics'] as const,
    data: (filters: Record<string, unknown>) =>
      [...queryKeys.safetyMetrics.all, 'data', filters] as const,
  },
  onboardingChecklists: {
    all: ['onboarding-checklists'] as const,
    lists: () => [...queryKeys.onboardingChecklists.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.onboardingChecklists.lists(), filters] as const,
    details: () => [...queryKeys.onboardingChecklists.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.onboardingChecklists.details(), id] as const,
    incompleteTechs: () => [...queryKeys.onboardingChecklists.all, 'incomplete-techs'] as const,
  },
  techOnboarding: {
    all: ['tech-onboarding'] as const,
    forTech: (techId: number) => [...queryKeys.techOnboarding.all, techId] as const,
  },
  performanceReviews: {
    all: ['performance-reviews'] as const,
    lists: () => [...queryKeys.performanceReviews.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.performanceReviews.lists(), filters] as const,
    summary: (techId: number) => [...queryKeys.performanceReviews.all, 'summary', techId] as const,
  },
  checkIns: {
    all: ['check-ins'] as const,
    lists: () => [...queryKeys.checkIns.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.checkIns.lists(), filters] as const,
    onSite: () => [...queryKeys.checkIns.all, 'on-site'] as const,
  },
  payrollReports: {
    all: ['payroll-reports'] as const,
    expenseByTech: (filters: Record<string, unknown>) =>
      [...queryKeys.payrollReports.all, 'expense-by-tech', filters] as const,
    expenseByProject: (filters: Record<string, unknown>) =>
      [...queryKeys.payrollReports.all, 'expense-by-project', filters] as const,
    payrollSummary: (filters: Record<string, unknown>) =>
      [...queryKeys.payrollReports.all, 'payroll-summary', filters] as const,
    costCodes: (filters: Record<string, unknown>) =>
      [...queryKeys.payrollReports.all, 'cost-codes', filters] as const,
    dashboardStats: () =>
      [...queryKeys.payrollReports.all, 'dashboard-stats'] as const,
  },
  // Session 5.6: H3Pay Enhancements
  quickbooks: {
    all: ['quickbooks'] as const,
    status: () => [...queryKeys.quickbooks.all, 'status'] as const,
  },
  xero: {
    all: ['xero'] as const,
    status: () => [...queryKeys.xero.all, 'status'] as const,
  },
  accountingIntegrations: {
    all: ['accounting-integrations'] as const,
    status: () => [...queryKeys.accountingIntegrations.all, 'status'] as const,
  },
  expensePolicies: {
    all: ['expense-policies'] as const,
    lists: () => [...queryKeys.expensePolicies.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.expensePolicies.lists(), filters] as const,
    details: () => [...queryKeys.expensePolicies.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.expensePolicies.details(), id] as const,
  },
  recurringExpenses: {
    all: ['recurring-expenses'] as const,
    lists: () => [...queryKeys.recurringExpenses.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.recurringExpenses.lists(), filters] as const,
    details: () => [...queryKeys.recurringExpenses.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.recurringExpenses.details(), id] as const,
  },
  // Session 5.8: H3Kit Job Packets
  jobPackets: {
    all: ['job-packets'] as const,
    lists: () => [...queryKeys.jobPackets.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.jobPackets.lists(), filters] as const,
    details: () => [...queryKeys.jobPackets.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.jobPackets.details(), id] as const,
  },
  // Session 5.7: Billing
  billing: {
    all: ['billing'] as const,
    plans: () => [...queryKeys.billing.all, 'plans'] as const,
    subscription: () => [...queryKeys.billing.all, 'subscription'] as const,
  },
  // Admin Module: Per-module roles
  moduleRoles: {
    all: ['module-roles'] as const,
    lists: () => [...queryKeys.moduleRoles.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.moduleRoles.lists(), filters] as const,
    details: () => [...queryKeys.moduleRoles.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.moduleRoles.details(), id] as const,
  },
  adminActivity: {
    all: ['admin-activity'] as const,
    lists: () => [...queryKeys.adminActivity.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.adminActivity.lists(), filters] as const,
  },
  // S11: User capability checklist
  userCapabilities: {
    all: ['user-capabilities'] as const,
    lists: () => [...queryKeys.userCapabilities.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.userCapabilities.lists(), filters] as const,
    details: () => [...queryKeys.userCapabilities.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.userCapabilities.details(), id] as const,
  },
  onboarding: {
    all: ['onboarding'] as const,
    state: () => [...queryKeys.onboarding.all, 'state'] as const,
  },
  memberships: {
    all: ['memberships'] as const,
    list: () => [...queryKeys.memberships.all, 'list'] as const,
  },
  dailyReports: {
    all: ['daily-reports'] as const,
    lists: () => [...queryKeys.dailyReports.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.dailyReports.lists(), filters] as const,
    details: () => [...queryKeys.dailyReports.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.dailyReports.details(), id] as const,
  },
  delayEntries: {
    all: ['delay-entries'] as const,
    lists: () => [...queryKeys.delayEntries.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.delayEntries.lists(), filters] as const,
  },
  // Procurement / Vendors (S16)
  vendors: {
    all: ['vendors'] as const,
    lists: () => [...queryKeys.vendors.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.vendors.lists(), filters] as const,
    details: () => [...queryKeys.vendors.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.vendors.details(), id] as const,
  },
  vendorContacts: {
    all: ['vendor-contacts'] as const,
    lists: () => [...queryKeys.vendorContacts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.vendorContacts.lists(), filters] as const,
  },
  vendorPriceLists: {
    all: ['vendor-price-lists'] as const,
    lists: () => [...queryKeys.vendorPriceLists.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.vendorPriceLists.lists(), filters] as const,
  },
  // Purchase Orders (S17)
  purchaseOrders: {
    all: ['purchase-orders'] as const,
    lists: () => [...queryKeys.purchaseOrders.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.purchaseOrders.lists(), filters] as const,
    details: () => [...queryKeys.purchaseOrders.all, 'detail'] as const,
    detail: (id: number) =>
      [...queryKeys.purchaseOrders.details(), id] as const,
  },
  purchaseRequisitions: {
    all: ['purchase-requisitions'] as const,
    lists: () => [...queryKeys.purchaseRequisitions.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.purchaseRequisitions.lists(), filters] as const,
    detail: (id: number) =>
      [...queryKeys.purchaseRequisitions.all, 'detail', id] as const,
  },
  poApprovalRules: {
    all: ['po-approval-rules'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.poApprovalRules.all, 'list', filters] as const,
  },
  receipts: {
    all: ['receipts'] as const,
    lists: () => [...queryKeys.receipts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.receipts.lists(), filters] as const,
    detail: (id: number) =>
      [...queryKeys.receipts.all, 'detail', id] as const,
  },
  // Board presence uses a separate top-level key so broad staffing-board
  // invalidations (e.g. from board mutations or realtime updates) don't
  // trigger the presence heartbeat to refetch.
  boardPresence: {
    all: ['board-presence'] as const,
    detail: (organizationId: number, userId: number) =>
      [...queryKeys.boardPresence.all, organizationId, userId] as const,
  },
  // H3ightboard
  staffingBoard: {
    all: ['staffing-board'] as const,
    timeline: (start: string, end: string) =>
      [...queryKeys.staffingBoard.all, 'timeline', start, end] as const,
    dispatches: () => [...queryKeys.staffingBoard.all, 'dispatches'] as const,
    notes: () => [...queryKeys.staffingBoard.all, 'notes'] as const,
    workPackage: (projectId: number) =>
      [...queryKeys.staffingBoard.all, 'workPackage', projectId] as const,
    supervisionCockpit: () => [...queryKeys.staffingBoard.all, 'supervisionCockpit'] as const,
    rollup: () => [...queryKeys.staffingBoard.all, 'rollup'] as const,
  },
  // Platform Admin
  platformAdmin: {
    all: ['platform-admin'] as const,
    stats: () => [...queryKeys.platformAdmin.all, 'stats'] as const,
    organizations: () => [...queryKeys.platformAdmin.all, 'organizations'] as const,
    organizationList: (filters: Record<string, unknown>) =>
      [...queryKeys.platformAdmin.organizations(), filters] as const,
    organizationDetail: (id: number) =>
      [...queryKeys.platformAdmin.organizations(), 'detail', id] as const,
    orgUsers: (orgId: number) =>
      [...queryKeys.platformAdmin.all, 'org-users', orgId] as const,
    orgModules: (orgId: number) =>
      [...queryKeys.platformAdmin.all, 'org-modules', orgId] as const,
    plans: () => [...queryKeys.platformAdmin.all, 'plans'] as const,
  },
  setupWizard: {
    all: ['setup-wizard'] as const,
    profile: () => [...queryKeys.setupWizard.all, 'profile'] as const,
  },
  generatedDocuments: {
    all: ['generated-documents'] as const,
    lists: () => [...queryKeys.generatedDocuments.all, 'list'] as const,
    details: () => [...queryKeys.generatedDocuments.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.generatedDocuments.details(), id] as const,
    content: (id: number) => [...queryKeys.generatedDocuments.detail(id), 'content'] as const,
    versions: (id: number) => [...queryKeys.generatedDocuments.detail(id), 'versions'] as const,
  },
  filedDocuments: {
    all: ['filed-documents'] as const,
    lists: () => [...queryKeys.filedDocuments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.filedDocuments.lists(), filters] as const,
    details: () => [...queryKeys.filedDocuments.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.filedDocuments.details(), id] as const,
  },
  // Core Foundation
  persons: {
    all: ['persons'] as const,
    lists: () => [...queryKeys.persons.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.persons.lists(), filters] as const,
    details: () => [...queryKeys.persons.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.persons.details(), id] as const,
  },
  cfLocations: {
    all: ['cf-locations'] as const,
    lists: () => [...queryKeys.cfLocations.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.cfLocations.lists(), filters] as const,
    details: () => [...queryKeys.cfLocations.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.cfLocations.details(), id] as const,
  },
  gearItems: {
    all: ['gear-items'] as const,
    lists: () => [...queryKeys.gearItems.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.gearItems.lists(), filters] as const,
    details: () => [...queryKeys.gearItems.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.gearItems.details(), id] as const,
    usage: () => [...queryKeys.gearItems.all, 'usage'] as const,
  },
  zones: {
    all: ['zones'] as const,
    lists: () => [...queryKeys.zones.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.zones.lists(), filters] as const,
    details: () => [...queryKeys.zones.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.zones.details(), id] as const,
  },
  spots: {
    all: ['spots'] as const,
    lists: () => [...queryKeys.spots.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.spots.lists(), filters] as const,
    details: () => [...queryKeys.spots.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.spots.details(), id] as const,
  },
  // Core Foundation — Location Templates
  locationTemplates: {
    all: ['location-templates'] as const,
    list: () => [...queryKeys.locationTemplates.all, 'list'] as const,
  },
  jobFolder: {
    all: ['job-folder'] as const,
    details: () => [...queryKeys.jobFolder.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.jobFolder.details(), id] as const,
  },
  // Gear Requests (G02/G03/GB06)
  gearRequests: {
    all: ['gear-requests'] as const,
    lists: () => [...queryKeys.gearRequests.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.gearRequests.lists(), filters] as const,
    details: () => [...queryKeys.gearRequests.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.gearRequests.details(), id] as const,
    opsReport: (filters: Record<string, unknown>) =>
      [...queryKeys.gearRequests.all, 'ops-report', filters] as const,
    board: (filters: Record<string, unknown>) =>
      [...queryKeys.gearRequests.all, 'board', filters] as const,
  },
  // Gear Request Templates (G08)
  gearRequestTemplates: {
    all: ['gear-request-templates'] as const,
    lists: () => [...queryKeys.gearRequestTemplates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.gearRequestTemplates.lists(), filters] as const,
    details: () => [...queryKeys.gearRequestTemplates.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.gearRequestTemplates.details(), id] as const,
  },
  // S10: Approval Delegation
  approvalDelegations: {
    all: ['approval-delegations'] as const,
    lists: () => [...queryKeys.approvalDelegations.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.approvalDelegations.lists(), filters] as const,
    details: () => [...queryKeys.approvalDelegations.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.approvalDelegations.details(), id] as const,
  },
} as const;

export default queryClient;
